/* global google */
import { App, reactive } from 'vue'
import { CURRENT_CONFIG } from '/@/api/http/config'

// Dark mode map styles
const darkModeStyles = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#263c3f' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b9a76' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#38414e' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#212a37' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9ca5b3' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#746855' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#1f2835' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#f3d19c' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#2f3948' }],
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#17263c' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#515c6d' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#17263c' }],
  },
]

// Dark mode for satellite/hybrid
const darkModeSatelliteStyles = [
  { elementType: 'labels.text.fill', stylers: [{ color: '#ffffff' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#000000' }, { weight: 2 }] },
]

export function useGMapManage () {
  const state = reactive({
    gMapApi: null as any,
    map: null as any,
    drawing: null as any,
    isLoaded: false,
    isDarkMode: false,
    currentMapType: 'hybrid' as string,
  })

  async function loadGoogleMaps (): Promise<any> {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        resolve(window.google.maps)
        return
      }

      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.google.maps))
        existingScript.addEventListener('error', reject)
        return
      }

      const script = document.createElement('script')
      script.src =
        `https://maps.googleapis.com/maps/api/js?key=${CURRENT_CONFIG.googleMapKey}&libraries=drawing,geometry`
      script.async = true
      script.defer = true
      script.onload = () => resolve(window.google.maps)
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  async function initMap (container: string, app: App) {
    try {
      const googleMaps = await loadGoogleMaps()

      state.gMapApi = googleMaps
      state.isLoaded = true

      const mapElement = document.getElementById(container)
      if (!mapElement) {
        console.error(`Map container element '${container}' not found`)
        return
      }

      // Start with a reasonable default - will auto-center when devices connect
      state.map = new google.maps.Map(mapElement, {
        center: { lat: 9.0820, lng: 8.6753 }, // Nigeria center as default
        zoom: 6,
        minZoom: 3, // Prevent zooming out too far (no repeated world)
        mapTypeId: google.maps.MapTypeId.HYBRID,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: google.maps.ControlPosition.TOP_RIGHT,
          mapTypeIds: [
            google.maps.MapTypeId.ROADMAP,
            google.maps.MapTypeId.SATELLITE,
            google.maps.MapTypeId.HYBRID,
            google.maps.MapTypeId.TERRAIN,
          ],
        },
        fullscreenControl: true,
        streetViewControl: false,
        zoomControl: true,
        scaleControl: true,
        rotateControl: true,
        gestureHandling: 'greedy',
        restriction: {
          latLngBounds: {
            north: 85,
            south: -85,
            west: -180,
            east: 180,
          },
          strictBounds: false,
        },
      })

      state.currentMapType = 'hybrid'

      // Listen for map type changes to apply appropriate dark mode
      state.map.addListener('maptypeid_changed', () => {
        state.currentMapType = state.map.getMapTypeId()
        if (state.isDarkMode) {
          applyDarkModeStyles()
        }
      })

      state.drawing = new google.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: true,
        drawingControlOptions: {
          position: google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [
            google.maps.drawing.OverlayType.MARKER,
            google.maps.drawing.OverlayType.POLYLINE,
            google.maps.drawing.OverlayType.POLYGON,
            google.maps.drawing.OverlayType.CIRCLE,
          ],
        },
        markerOptions: {
          draggable: true,
        },
        polylineOptions: {
          strokeColor: '#2d8cf0',
          strokeOpacity: 1,
          strokeWeight: 2,
          editable: true,
        },
        polygonOptions: {
          strokeColor: '#2d8cf0',
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: '#1791fc',
          fillOpacity: 0.4,
          editable: true,
        },
        circleOptions: {
          strokeColor: '#2d8cf0',
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: '#1791fc',
          fillOpacity: 0.4,
          editable: true,
        },
      })

      state.drawing.setMap(state.map)

      app.config.globalProperties.$gMapApi = state.gMapApi
      app.config.globalProperties.$map = state.map
      app.config.globalProperties.$drawing = state.drawing

      console.log('Google Maps initialized successfully')
    } catch (e) {
      console.error('Google Maps failed to load:', e)
    }
  }

  function applyDarkModeStyles () {
    if (!state.map) return

    const mapType = state.map.getMapTypeId()
    if (mapType === 'satellite' || mapType === 'hybrid') {
      state.map.setOptions({ styles: darkModeSatelliteStyles })
    } else {
      state.map.setOptions({ styles: darkModeStyles })
    }
  }

  function clearMapStyles () {
    if (!state.map) return
    state.map.setOptions({ styles: [] })
  }

  /**
   * Toggle dark mode on/off
   */
  function toggleDarkMode (): boolean {
    state.isDarkMode = !state.isDarkMode

    if (state.isDarkMode) {
      applyDarkModeStyles()
    } else {
      clearMapStyles()
    }

    return state.isDarkMode
  }

  /**
   * Set dark mode explicitly
   */
  function setDarkMode (enabled: boolean): void {
    state.isDarkMode = enabled

    if (enabled) {
      applyDarkModeStyles()
    } else {
      clearMapStyles()
    }
  }

  /**
   * Check if dark mode is enabled
   */
  function isDarkMode (): boolean {
    return state.isDarkMode
  }

  function globalPropertiesConfig (app: App) {
    initMap('g-container', app)
  }

  function getMapInstance (): any {
    return state.map
  }

  function getDrawingManager (): any {
    return state.drawing
  }

  function isMapLoaded (): boolean {
    return state.isLoaded
  }

  return {
    globalPropertiesConfig,
    getMapInstance,
    getDrawingManager,
    isMapLoaded,
    toggleDarkMode,
    setDarkMode,
    isDarkMode,
  }
}
